<template>
  <div class="field no-select">
    <!-- TODO: also remove from metadata
    <label v-if="label" class="m-0 label no-select d-block">
      <h3>{{ label }}</h3>
    </label> -->

    <!-- TODO: Use UI type and set model value/tab in watcher -->
    <VueSelect
      v-if="showTypeOptions"
      :options="typeOptions"
      :value="type"
      label="Link para"
      @change="type = $event"
    />

    <div
      v-if="isExternalLink"
      class="field no-select"
    >
      <ShortText
        :name="`${name}.value`"
        type="url"
        :label="label"
        :help-text="
          hasHelpText
            ? finalHelpText
            : 'Precisa começar com http:// ou https://'
        "
      />
    </div>

    <div
      v-if="isAnchor"
      class="field no-select"
    >
      <ShortText
        :name="`${name}.value.anchor`"
        label="Digite o ID do módulo"
        help-text='Você encontra o ID do módulo em "Dados do módulo" no menu de edição'
      />
    </div>

    <div
      v-if="isPage || isAnchor"
      class="field no-select"
    >
      <VueSelect
        :options="pageOptions"
        :name="`${name}.${isPage ? `value` : 'value.page'}`"
        label="Selecione a página"
        help-text="Páginas criadas anteriormente no Lets Sites"
      />
    </div>

    <template v-if="isFile">
      <label
        class="label no-select label--secondary mb-1"
        :for="getIdForField('file')"
      >
        <h4>Arquivo</h4>
      </label>
      <br />
      <File
        :id="getIdForField('file')"
        :name="`${name}.value`"
        :max-file-size="25"
        help-text="São aceitos arquivos do tipo: PDF, Docx, JPG e PNG."
      />
    </template>

    <Checkbox
      v-if="allowNewTab && type !== 'defaultLink'"
      :name="`${name}.openInNewTab`"
      label="Abrir em nova aba"
      class="mt-3"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import commonProperties from './common-properties';
import ShortText from './ShortText.vue';
import VueSelect from './VueSelect.vue';
import Checkbox from './Checkbox.vue';
import File from './File.vue';

export default {
  name: 'Url',
  components: {
    ShortText,
    VueSelect,
    Checkbox,
    File,
  },
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    availableTypes: {
      type: Array,
      default() {
        return ['externalLink', 'page', 'anchor', 'file'];
      },
    },
    default: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    allowNewTab: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isAnchor() {
      return this.type === 'anchor';
    },
    isExternalLink() {
      return this.type === 'externalLink';
    },
    isPage() {
      return this.type === 'page';
    },
    isFile() {
      return this.type === 'file';
    },
    pageOptions() {
      // If needed, remember this UI type in settings view will use language pt
      const { language } = this.activeGlobalData || this.container;
      const pages = this.pagesArray(language);

      return [
        {
          label: 'Selecione uma página',
          value: '',
        },
        ...pages.map((page) => ({
          label: page.title,
          isPublished: page.isPublished,
          value: page.id,
        })),
      ];
    },
    showTypeOptions() {
      return this.typeOptions.length > 1;
    },
    type: {
      get() {
        return this.model?.type || 'externalLink';
      },
      set(type) {
        let value = '';
        let openInNewTab = false;

        if (type === 'externalLink' || type === 'file') {
          openInNewTab = true;
        }

        if (type === 'anchor') {
          value = {
            anchor: '',
            page: '',
          };

          if (!this.activeGlobalData && this.activePage?.id) {
            value.page = this.activePage.id;
          }
        }

        this.model = {
          type,
          value,
          openInNewTab,
        };
      },
    },
    typeOptions() {
      const options = [
        {
          label: 'Link padrão',
          value: 'defaultLink', // este tipo não está incluso por padrão no 'availableTypes' e não faz nada sozinho, o 'link padrão' deve ser tratado no módulo que optar por sua utilização
        },
        {
          label: 'Link externo',
          value: 'externalLink',
        },
        {
          label: 'Link para página do site',
          value: 'page',
        },
        {
          label: 'Link para um módulo',
          value: 'anchor',
        },
        {
          label: 'Link para um arquivo',
          value: 'file',
        },
      ];

      return options.filter((option) =>
        this.availableTypes.includes(option.value),
      );
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapState(['pages']),
    ...mapGetters([
      'activePage',
      'activeGlobalData',
      'container',
      'pagesArray',
    ]),
    ...commonProperties.computed,
  },
  mounted() {
    if (!this.availableTypes.includes(this.type)) {
      [this.type] = this.availableTypes;
    }
  },
  methods: {
    getIdForField(field) {
      return `${this.name}-${field}`;
    },
  },
};
</script>

<style scoped lang="scss">
h3,
h4 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}

h4 {
  margin: 0;
}

input.form-control {
  border-bottom: 1px solid #e8e8e8;

  &:invalid {
    border-color: #e0414b;
    border-width: 2px;
    box-shadow: none;
  }
}

label:not(:first-of-type) {
  margin-top: 24px;
}
</style>
